// P9.1 — Objective, versioned market-structure primitives (deliverable 5).
//
// Every concept here has an OPERATIONAL definition: deterministic functions over completed
// bars, with frozen parameters and — critically — an explicit `confirmedAt` index on every
// event: the FIRST bar index at which the event is knowable without lookahead. A swing high
// of strength k does not exist until k bars after its extreme; an FVG exists at the close of
// its third candle; a sweep exists at the close of the bar that wicked the level and closed
// back. Consumers (the setup state machine) may only react at bar >= confirmedAt. This is the
// no-lookahead contract, enforced by tests (scripts/test-setup-engine.mjs).
//
// Versioning: STRUCTURAL_VOCABULARY_VERSION stamps every emitted event; definition changes
// bump the version (never silent redefinition). Ambiguous chart terminology WITHOUT an
// objective definition lives in the terminology registry (research/p9.1/TERMINOLOGY_REGISTRY
// .json) as not_yet_machine_testable — it is NOT encoded here.
//
// Purity: no I/O, no clock, no randomness.

export const STRUCTURAL_VOCABULARY_VERSION = 1;

export interface Bar { timestamp: number; open: number; high: number; low: number; close: number; volume: number }

export type StructuralConceptId =
  | 'swing_high' | 'swing_low' | 'equal_highs' | 'equal_lows'
  | 'buyside_liquidity' | 'sellside_liquidity' | 'liquidity_sweep'
  | 'displacement' | 'fair_value_gap' | 'inverse_fair_value_gap' | 'projected_fvg'
  | 'order_block' | 'breaker' | 'dealing_range' | 'range_location' | 'ote_band'
  | 'structure_break' | 'failed_structure_break' | 'reclaim' | 'candle_violation'
  | 'imbalance_mitigation' | 'opposing_liquidity_target' | 'measured_range_target'
  | 'session_window' | 'reference_candle';

/** One structural event, knowable at `confirmedAt` (inclusive) and never earlier. */
export interface StructuralEvent {
  concept: StructuralConceptId;
  version: number;
  /** Bar index of the structural anchor (extreme / candle 1 / sweep bar…). */
  refIndex: number;
  /** FIRST index at which this event is knowable (>= refIndex). */
  confirmedAt: number;
  direction: 'bullish' | 'bearish' | 'neutral';
  /** Price level (single-level concepts) — e.g. a swept liquidity level. */
  level?: number;
  /** Price zone [lo, hi] (zone concepts: FVG, OB, breaker, range, OTE). */
  zone?: [number, number];
  meta?: Record<string, number | string | boolean>;
}

const ev = (e: Omit<StructuralEvent, 'version'>): StructuralEvent => ({ ...e, version: STRUCTURAL_VOCABULARY_VERSION });

// ── shared measures ───────────────────────────────────────────────────────────

/** Simple ATR (mean true range over n completed bars ending at i, inclusive). */
export function atrAt(bars: readonly Bar[], i: number, n = 14): number {
  const lo = Math.max(1, i - n + 1);
  let s = 0, c = 0;
  for (let k = lo; k <= i; k++) {
    const tr = Math.max(bars[k].high - bars[k].low, Math.abs(bars[k].high - bars[k - 1].close), Math.abs(bars[k].low - bars[k - 1].close));
    s += tr; c++;
  }
  return c ? s / c : 0;
}

// ── swings & liquidity ────────────────────────────────────────────────────────

/** Swing high of strength k at bar i: high[i] strictly exceeds highs of the k bars on each
 *  side. KNOWABLE only at i+k (the right shoulder must complete) — confirmedAt = i+k. */
export function swingHighs(bars: readonly Bar[], strength = 2): StructuralEvent[] {
  const out: StructuralEvent[] = [];
  for (let i = strength; i < bars.length - strength; i++) {
    let ok = true;
    for (let k = 1; k <= strength; k++) if (!(bars[i].high > bars[i - k].high && bars[i].high > bars[i + k].high)) { ok = false; break; }
    if (ok) out.push(ev({ concept: 'swing_high', refIndex: i, confirmedAt: i + strength, direction: 'bearish', level: bars[i].high, meta: { strength } }));
  }
  return out;
}
export function swingLows(bars: readonly Bar[], strength = 2): StructuralEvent[] {
  const out: StructuralEvent[] = [];
  for (let i = strength; i < bars.length - strength; i++) {
    let ok = true;
    for (let k = 1; k <= strength; k++) if (!(bars[i].low < bars[i - k].low && bars[i].low < bars[i + k].low)) { ok = false; break; }
    if (ok) out.push(ev({ concept: 'swing_low', refIndex: i, confirmedAt: i + strength, direction: 'bullish', level: bars[i].low, meta: { strength } }));
  }
  return out;
}

/** Equal highs/lows: two swing extremes within tolAtr × ATR of each other, no intervening
 *  higher extreme. Confirmed when the SECOND swing confirms. The shared level is resting
 *  liquidity (buy-side above equal highs; sell-side below equal lows). */
export function equalExtremes(bars: readonly Bar[], kind: 'equal_highs' | 'equal_lows', strength = 2, tolAtr = 0.25, atrN = 14): StructuralEvent[] {
  const swings = kind === 'equal_highs' ? swingHighs(bars, strength) : swingLows(bars, strength);
  const out: StructuralEvent[] = [];
  for (let a = 0; a < swings.length - 1; a++) {
    const b = a + 1;
    const tol = tolAtr * atrAt(bars, swings[b].refIndex, atrN);
    if (Math.abs((swings[a].level ?? 0) - (swings[b].level ?? 0)) <= tol) {
      const lvl = kind === 'equal_highs' ? Math.max(swings[a].level!, swings[b].level!) : Math.min(swings[a].level!, swings[b].level!);
      out.push(ev({ concept: kind, refIndex: swings[b].refIndex, confirmedAt: swings[b].confirmedAt, direction: kind === 'equal_highs' ? 'bearish' : 'bullish', level: lvl, meta: { firstRef: swings[a].refIndex } }));
    }
  }
  return out;
}

/** Liquidity pools: buy-side = levels above price (swing highs / equal highs); sell-side =
 *  below (swing lows / equal lows). A pool exists from its swing's confirmedAt until swept. */
export function liquidityPools(bars: readonly Bar[], side: 'buyside' | 'sellside', strength = 2): StructuralEvent[] {
  const base = side === 'buyside' ? swingHighs(bars, strength) : swingLows(bars, strength);
  return base.map((s) => ev({ concept: side === 'buyside' ? 'buyside_liquidity' : 'sellside_liquidity', refIndex: s.refIndex, confirmedAt: s.confirmedAt, direction: s.direction, level: s.level, meta: { strength } }));
}

/** Liquidity sweep (raid): a bar TRADES beyond a resting pool level but CLOSES back on the
 *  original side. Confirmed at that bar's close. Only pools already confirmed BEFORE the
 *  sweep bar are eligible (no self-referencing lookahead). */
export function liquiditySweeps(bars: readonly Bar[], side: 'buyside' | 'sellside', strength = 2): StructuralEvent[] {
  const pools = liquidityPools(bars, side, strength);
  const out: StructuralEvent[] = [];
  for (const p of pools) {
    for (let i = p.confirmedAt + 1; i < bars.length; i++) {
      const beyond = side === 'buyside' ? bars[i].high > p.level! : bars[i].low < p.level!;
      const closedBack = side === 'buyside' ? bars[i].close < p.level! : bars[i].close > p.level!;
      const closedThrough = side === 'buyside' ? bars[i].close > p.level! : bars[i].close < p.level!;
      if (beyond && closedBack) {
        out.push(ev({ concept: 'liquidity_sweep', refIndex: i, confirmedAt: i, direction: side === 'buyside' ? 'bearish' : 'bullish', level: p.level, meta: { pooledRef: p.refIndex, side } }));
        break; // a pool is consumed by its first sweep
      }
      if (closedThrough) break; // level broken, not swept — pool consumed without a sweep
    }
  }
  return out.sort((a, b) => a.confirmedAt - b.confirmedAt);
}

// ── displacement & imbalances ─────────────────────────────────────────────────

/** Displacement: a single candle whose BODY ≥ minBodyAtr × ATR in the stated direction.
 *  Confirmed at its close. */
export function displacements(bars: readonly Bar[], direction: 'bullish' | 'bearish', minBodyAtr = 1.5, atrN = 14): StructuralEvent[] {
  const out: StructuralEvent[] = [];
  for (let i = atrN + 1; i < bars.length; i++) {
    const body = bars[i].close - bars[i].open;
    const a = atrAt(bars, i - 1, atrN); // ATR of PRIOR bars only (no self-inclusion)
    if (a <= 0) continue;
    if (direction === 'bullish' && body >= minBodyAtr * a) out.push(ev({ concept: 'displacement', refIndex: i, confirmedAt: i, direction, meta: { bodyAtr: body / a } }));
    if (direction === 'bearish' && -body >= minBodyAtr * a) out.push(ev({ concept: 'displacement', refIndex: i, confirmedAt: i, direction, meta: { bodyAtr: -body / a } }));
  }
  return out;
}

/** Fair value gap (three-candle imbalance). Bullish: low(c3) > high(c1) — zone
 *  [high(c1), low(c3)]. Bearish: high(c3) < low(c1) — zone [high(c3), low(c1)].
 *  refIndex = middle candle; confirmed at candle-3 close. */
export function fairValueGaps(bars: readonly Bar[], direction: 'bullish' | 'bearish'): StructuralEvent[] {
  const out: StructuralEvent[] = [];
  for (let i = 2; i < bars.length; i++) {
    if (direction === 'bullish' && bars[i].low > bars[i - 2].high)
      out.push(ev({ concept: 'fair_value_gap', refIndex: i - 1, confirmedAt: i, direction, zone: [bars[i - 2].high, bars[i].low] }));
    if (direction === 'bearish' && bars[i].high < bars[i - 2].low)
      out.push(ev({ concept: 'fair_value_gap', refIndex: i - 1, confirmedAt: i, direction, zone: [bars[i].high, bars[i - 2].low] }));
  }
  return out;
}

/** Inverse FVG: an FVG whose zone is later CLOSED THROUGH (a close fully beyond the far
 *  edge against the gap's direction); it inverts and acts as the opposite-side zone.
 *  Confirmed at the closing-through bar. */
export function inverseFairValueGaps(bars: readonly Bar[], originalDirection: 'bullish' | 'bearish'): StructuralEvent[] {
  const gaps = fairValueGaps(bars, originalDirection);
  const out: StructuralEvent[] = [];
  for (const g of gaps) {
    const [lo, hi] = g.zone!;
    for (let i = g.confirmedAt + 1; i < bars.length; i++) {
      const closedThrough = originalDirection === 'bullish' ? bars[i].close < lo : bars[i].close > hi;
      if (closedThrough) {
        out.push(ev({ concept: 'inverse_fair_value_gap', refIndex: g.refIndex, confirmedAt: i, direction: originalDirection === 'bullish' ? 'bearish' : 'bullish', zone: g.zone, meta: { invertedAt: i } }));
        break;
      }
    }
  }
  return out;
}

/** Order block: the LAST opposite-direction candle strictly before a qualifying displacement.
 *  Zone = that candle's full range (v1 parameterizable to body). Confirmed when the
 *  displacement confirms. */
export function orderBlocks(bars: readonly Bar[], displacementDirection: 'bullish' | 'bearish', minBodyAtr = 1.5, atrN = 14): StructuralEvent[] {
  const disp = displacements(bars, displacementDirection, minBodyAtr, atrN);
  const out: StructuralEvent[] = [];
  for (const d of disp) {
    for (let j = d.refIndex - 1; j >= Math.max(0, d.refIndex - 10); j--) {
      const opp = displacementDirection === 'bullish' ? bars[j].close < bars[j].open : bars[j].close > bars[j].open;
      if (opp) {
        out.push(ev({ concept: 'order_block', refIndex: j, confirmedAt: d.confirmedAt, direction: displacementDirection, zone: [bars[j].low, bars[j].high], meta: { displacementRef: d.refIndex } }));
        break;
      }
    }
  }
  return out;
}

/** Breaker: an order block whose zone is later CLOSED THROUGH against its direction; on
 *  return it acts as the opposite-side zone. Confirmed at the break-through close. */
export function breakers(bars: readonly Bar[], obDirection: 'bullish' | 'bearish', minBodyAtr = 1.5, atrN = 14): StructuralEvent[] {
  const obs = orderBlocks(bars, obDirection, minBodyAtr, atrN);
  const out: StructuralEvent[] = [];
  for (const ob of obs) {
    const [lo, hi] = ob.zone!;
    for (let i = ob.confirmedAt + 1; i < bars.length; i++) {
      const broke = obDirection === 'bullish' ? bars[i].close < lo : bars[i].close > hi;
      if (broke) { out.push(ev({ concept: 'breaker', refIndex: ob.refIndex, confirmedAt: i, direction: obDirection === 'bullish' ? 'bearish' : 'bullish', zone: ob.zone, meta: { brokeAt: i } })); break; }
    }
  }
  return out;
}

// ── dealing range, location, OTE ──────────────────────────────────────────────

/** Dealing range v1: the most recent CONFIRMED swing-high/swing-low pair as of bar i
 *  (both confirmedAt <= i), high above low. Returns null when structure is one-sided. */
export function dealingRangeAt(bars: readonly Bar[], i: number, strength = 2): StructuralEvent | null {
  const sh = swingHighs(bars, strength).filter((s) => s.confirmedAt <= i);
  const sl = swingLows(bars, strength).filter((s) => s.confirmedAt <= i);
  if (!sh.length || !sl.length) return null;
  const h = sh[sh.length - 1], l = sl[sl.length - 1];
  if (h.level! <= l.level!) return null;
  return ev({ concept: 'dealing_range', refIndex: Math.max(h.refIndex, l.refIndex), confirmedAt: Math.max(h.confirmedAt, l.confirmedAt), direction: 'neutral', zone: [l.level!, h.level!], meta: { highRef: h.refIndex, lowRef: l.refIndex } });
}

/** Location of price within a range: fraction 0 (range low) … 1 (range high); premium >
 *  0.5, discount < 0.5; quadrants configurable by the caller. */
export function rangeLocation(price: number, range: [number, number]): number {
  const [lo, hi] = range;
  return hi > lo ? (price - lo) / (hi - lo) : 0.5;
}

/** OTE-like retracement band of a displacement leg: the [0.62, 0.79] retracement zone
 *  (params frozen per setup). For a bearish leg the band sits above; bullish below. */
export function oteBand(legStart: number, legEnd: number, lo = 0.62, hi = 0.79): [number, number] {
  const a = legEnd - (legEnd - legStart) * hi;
  const b = legEnd - (legEnd - legStart) * lo;
  return a <= b ? [a, b] : [b, a];
}

// ── structure breaks, reclaims, mitigation, references ────────────────────────

/** Structure break: a CLOSE beyond the most recent confirmed opposing swing point.
 *  Confirmed at that close. failed_structure_break: traded beyond intrabar but closed back,
 *  then closed back through the OTHER way within failBars. */
export function structureBreaks(bars: readonly Bar[], direction: 'bullish' | 'bearish', strength = 2): StructuralEvent[] {
  const swings = direction === 'bullish' ? swingHighs(bars, strength) : swingLows(bars, strength);
  const out: StructuralEvent[] = [];
  for (const s of swings) {
    for (let i = s.confirmedAt + 1; i < bars.length; i++) {
      const broke = direction === 'bullish' ? bars[i].close > s.level! : bars[i].close < s.level!;
      if (broke) { out.push(ev({ concept: 'structure_break', refIndex: i, confirmedAt: i, direction, level: s.level, meta: { sweptSwingRef: s.refIndex } })); break; }
    }
  }
  return out.sort((a, b) => a.confirmedAt - b.confirmedAt);
}

/** Reclaim: after a close beyond `level` (violation), a close back on the original side
 *  within `withinBars`. Confirmed at the reclaiming close. */
export function reclaims(bars: readonly Bar[], level: number, violatedSide: 'above' | 'below', fromIndex: number, withinBars: number): StructuralEvent | null {
  for (let i = fromIndex + 1; i <= Math.min(bars.length - 1, fromIndex + withinBars); i++) {
    const back = violatedSide === 'above' ? bars[i].close < level : bars[i].close > level;
    if (back) return ev({ concept: 'reclaim', refIndex: fromIndex, confirmedAt: i, direction: violatedSide === 'above' ? 'bearish' : 'bullish', level });
  }
  return null;
}

/** Candle-reference violation: a CLOSE beyond a reference candle's high/low. */
export function candleViolation(bars: readonly Bar[], refIndex: number, side: 'high' | 'low', fromIndex: number): StructuralEvent | null {
  const level = side === 'high' ? bars[refIndex].high : bars[refIndex].low;
  for (let i = Math.max(fromIndex, refIndex + 1); i < bars.length; i++) {
    const broke = side === 'high' ? bars[i].close > level : bars[i].close < level;
    if (broke) return ev({ concept: 'candle_violation', refIndex, confirmedAt: i, direction: side === 'high' ? 'bullish' : 'bearish', level, meta: { side } });
  }
  return null;
}

/** Imbalance mitigation: first TOUCH of a zone at/after `fromIndex` (price trades into it). */
export function zoneMitigation(bars: readonly Bar[], zone: [number, number], fromIndex: number): StructuralEvent | null {
  const [lo, hi] = zone;
  for (let i = fromIndex; i < bars.length; i++) {
    if (bars[i].low <= hi && bars[i].high >= lo) return ev({ concept: 'imbalance_mitigation', refIndex: i, confirmedAt: i, direction: 'neutral', zone });
  }
  return null;
}

// ── targets ───────────────────────────────────────────────────────────────────

/** Opposing-liquidity target: nearest confirmed pool on the trade's profit side as of
 *  entryIndex (pools confirmed <= entryIndex; level beyond entryPrice). */
export function opposingLiquidityTarget(bars: readonly Bar[], direction: 'long' | 'short', entryIndex: number, entryPrice: number, strength = 2): StructuralEvent | null {
  const pools = liquidityPools(bars, direction === 'long' ? 'buyside' : 'sellside', strength).filter((p) => p.confirmedAt <= entryIndex);
  const eligible = pools.filter((p) => (direction === 'long' ? p.level! > entryPrice : p.level! < entryPrice));
  if (!eligible.length) return null;
  eligible.sort((a, b) => direction === 'long' ? a.level! - b.level! : b.level! - a.level!);
  const t = eligible[0];
  return ev({ concept: 'opposing_liquidity_target', refIndex: t.refIndex, confirmedAt: entryIndex, direction: direction === 'long' ? 'bullish' : 'bearish', level: t.level });
}

/** Measured-range target: project `mult` × range height from the breakout edge. */
export function measuredRangeTarget(range: [number, number], direction: 'long' | 'short', mult = 1): StructuralEvent {
  const [lo, hi] = range;
  const h = hi - lo;
  const level = direction === 'long' ? hi + mult * h : lo - mult * h;
  return ev({ concept: 'measured_range_target', refIndex: -1, confirmedAt: -1, direction: direction === 'long' ? 'bullish' : 'bearish', level, meta: { mult } });
}

// ── registry (for docs/versioning; the compiler cites these) ──────────────────

export const STRUCTURAL_DEFINITIONS: Record<StructuralConceptId, string> = {
  swing_high: 'high strictly above the k highs on each side; knowable k bars after the extreme',
  swing_low: 'low strictly below the k lows on each side; knowable k bars after the extreme',
  equal_highs: 'two consecutive swing highs within tolAtr×ATR; level = the higher; confirmed with the second swing',
  equal_lows: 'two consecutive swing lows within tolAtr×ATR; level = the lower; confirmed with the second swing',
  buyside_liquidity: 'resting stops above a confirmed swing/equal high (level = its high)',
  sellside_liquidity: 'resting stops below a confirmed swing/equal low (level = its low)',
  liquidity_sweep: 'a bar trades beyond a confirmed pool level and CLOSES back on the original side; consumed on first sweep; a close-through is a break, not a sweep',
  displacement: 'single candle body >= minBodyAtr × ATR(prior bars) in the stated direction',
  fair_value_gap: 'three-candle imbalance: c3 low > c1 high (bullish) / c3 high < c1 low (bearish); zone between; confirmed at c3 close',
  inverse_fair_value_gap: 'an FVG later CLOSED fully through against its direction; inverts to the opposite-side zone',
  projected_fvg: 'an HTF fair value gap projected as a zone onto LTF bars via completed-bar mapping (multiTimeframe.projectZone)',
  order_block: 'last opposite-direction candle before a qualifying displacement; zone = its range; confirmed with the displacement',
  breaker: 'an order block later CLOSED through against its direction; on return acts as the opposite-side zone',
  dealing_range: 'most recent confirmed swing-high/low pair (both confirmed as-of the query bar)',
  range_location: 'price position in [rangeLow,rangeHigh] as a 0..1 fraction; premium>0.5, discount<0.5; quadrants configurable',
  ote_band: 'the [0.62,0.79] retracement band of a stated leg (parameters frozen per setup)',
  structure_break: 'a CLOSE beyond the most recent confirmed opposing swing point',
  failed_structure_break: 'traded beyond intrabar, closed back, and closed through the other way within failBars (v1: composed from sweep+reclaim)',
  reclaim: 'after a violating close beyond a level, a close back on the original side within withinBars',
  candle_violation: 'a CLOSE beyond a reference candle high/low',
  imbalance_mitigation: 'first touch of a zone at/after a given index',
  opposing_liquidity_target: 'nearest confirmed opposing pool beyond entry as-of entry (no future pools)',
  measured_range_target: 'breakout edge +/- mult × range height',
  session_window: 'clock-time window in exchange-local minutes (timezone/DST resolved in multiTimeframe)',
  reference_candle: 'a deterministically selected candle (e.g. first completed candle of a session window) whose high/low become reference levels',
};
